import { useEffect, useRef } from 'react'

import { subscribeToLocationPin } from '@/map'
import { gis } from '@/server-data'

import { LocationEditorFieldConfig, LocationEditorFieldValue } from '../types'

type OnChange = (value: LocationEditorFieldValue) => void

export function useConnectLocationPinToFormField(
  props: LocationEditorFieldConfig,
  location: LocationEditorFieldValue,
  onChange: OnChange,
) {
  const { pinId, disabled, onRadiusChanged, pinRadius } = props

  const api = useRef({ formLocation: location, onChange, onRadiusChanged, pinRadius })
  useEffect(() => {
    api.current = { formLocation: location, onChange, onRadiusChanged, pinRadius }
  }, [location, onChange, onRadiusChanged, pinRadius])

  // listen to pin changes
  useEffect(() => {
    if (disabled) return
    let effectCleared = false

    const unsubscribe = subscribeToLocationPin(pinId, pin => {
      if (effectCleared) return
      if (!pin) return

      const { formLocation, onChange } = api.current
      const location = pin.pin.location

      if (!location) return
      if (!formLocation) return

      // Update Form Location if the pin has been moved
      if (
        !gis.latLngEquals(formLocation.latLng, location.latLng) ||
        formLocation.address !== location.address ||
        formLocation.geoAddress !== location.geoAddress ||
        formLocation.status !== location.status
      ) {
        // update form location
        onChange(location)
      }

      const { onRadiusChanged, pinRadius } = api.current

      // Update Form radius if the pin radius has been changed
      if (onRadiusChanged && typeof pinRadius === 'number' && pinRadius !== pin.pin.radius) {
        onRadiusChanged(pin.pin.radius, pinRadius)
      }
    })

    return () => {
      effectCleared = true
      unsubscribe()
    }
  }, [pinId, disabled])
}
